import { useState } from "react";
import type { FeedSignal } from "../types";
import { FEED_PAGE_SIZE, feedFooterMode } from "../hooks";
import { SignalCard } from "./SignalCard";
import {
  FeedEmptyState,
  FeedErrorState,
  FeedLoadingSkeleton,
} from "./FeedStates";

/**
 * Redesign · feed (sealed mock). Plain-flow single column — the mock renders the
 * cards in normal flow with the page scrolling, so the windowing virtualizer is
 * retired and the "Show N more" button bounds the DOM instead.
 *
 * Pagination is keyset underneath, never infinite scroll: each reveal shows one
 * more page's worth of cards and pulls the next keyset page when the reveal
 * outruns what is loaded. The parent remounts this component per tab + type
 * filter, so the reveal count resets to the top of each list.
 */

const STEP = FEED_PAGE_SIZE;
const INITIAL_VISIBLE = FEED_PAGE_SIZE;

export function SignalFeed({
  signals,
  status,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRetry,
  onClearFilters,
  viewerId,
  practiceEmptyDisplayName = null,
}: {
  signals: FeedSignal[];
  /** Retained for the caller contract; the footer gates on hasNextPage, never on
   *  this counts.all-derived total (a stale count must not gate the control). */
  total: number;
  status: "pending" | "error" | "success";
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onRetry: () => void;
  onClearFilters: () => void;
  viewerId: string;
  /**
   * Set only when a practice is selected AND the server's own
   * practice_lens.total_count for it is genuinely zero (2W) — never derived
   * from the nav search narrowing the loaded page to zero. Drives the
   * explicit "not yet routed" empty-state copy instead of the ordinary one.
   */
  practiceEmptyDisplayName?: string | null;
}): JSX.Element {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const shown = Math.min(visibleCount, signals.length);
  const displayed = signals.slice(0, shown);

  const showMore = () => {
    const next = visibleCount + STEP;
    setVisibleCount(next);
    // Pull the next keyset page if the reveal outruns what is loaded.
    if (next > signals.length && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  if (status === "pending") return <FeedLoadingSkeleton />;
  if (status === "error") return <FeedErrorState onRetry={onRetry} />;
  if (signals.length === 0) {
    return (
      <FeedEmptyState
        onClearFilters={onClearFilters}
        practiceDisplayName={practiceEmptyDisplayName}
      />
    );
  }

  const footer = feedFooterMode(hasNextPage, signals.length);

  return (
    <div>
      <div className="sx-feed">
        {displayed.map((signal) => (
          <SignalCard key={signal.id} signal={signal} viewerId={viewerId} />
        ))}
      </div>

      {footer === "more" ? (
        <div className="sx-more-row">
          <button
            type="button"
            className="sx-btn-more"
            onClick={showMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : "Show more"}
          </button>
        </div>
      ) : footer === "end" ? (
        <div className="sx-more-row">
          {/* Honest terminal state — muted sealed --meta, no accent, and no
              counts.all-derived numeric total. */}
          <p
            className="sx-feed-end"
            role="status"
            style={{ margin: 0, color: "var(--meta)", fontSize: "13px" }}
          >
            End of signals.
          </p>
        </div>
      ) : null}
    </div>
  );
}
